import {
  effectScope,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type WatchSource,
} from "vue";
import { useVueApp } from "./use-vue-app";

export interface ServiceDefinition<TService, TDeps = void> {
  /**
   * Dynamic import that returns the factory function.
   */
  load: () => Promise<{ create: (deps: TDeps) => TService }>;
}

export interface LazyServicesOptions<
  TConfig extends Record<string, boolean>,
  TServices extends Record<keyof TConfig, unknown>,
  TDeps,
> {
  /**
   * Reactive config where keys are service names and values are whether to load them.
   */
  config: WatchSource<TConfig>;

  /**
   * Map of service name to its definition (how to load it).
   */
  definitions: { [K in keyof TConfig]: ServiceDefinition<TServices[K], TDeps> };

  /**
   * Dependencies to pass to each service factory.
   */
  deps: MaybeRefOrGetter<TDeps>;
}

/**
 * Composable for lazy-loading multiple services based on a boolean config.
 *
 * Uses watch + effectScope pattern for proper cleanup when config changes.
 *
 * @example
 * const { services, isReady } = useLazyServices({
 *   config: () => ({
 *     membership: capabilities.value?.memberships_enabled ?? false,
 *     token: capabilities.value?.wallet_enabled ?? false,
 *   }),
 *   definitions: {
 *     membership: {
 *       load: () => import("./membership-strategy").then(m => ({ create: m.createMembershipStrategy })),
 *     },
 *     token: {
 *       load: () => import("./token-strategy").then(m => ({ create: m.createTokenStrategy })),
 *     },
 *   },
 *   deps: { tokenBalanceMap },
 * });
 */
export function useLazyServices<
  TKeys extends string,
  TConfig extends Record<TKeys, boolean>,
  TServices extends Record<keyof TConfig, unknown>,
  TDeps,
>(options: LazyServicesOptions<TConfig, TServices, TDeps>) {
  const services = ref<Partial<TServices>>({});
  const isReady = ref(false);
  const app = useVueApp();

  watch(
    options.config,
    async (config, _, onCleanup) => {
      isReady.value = false;
      const scope = effectScope();

      // Load all enabled services in parallel
      const entries = Object.entries(config) as [TKeys, boolean][];
      const enabledKeys = entries
        .filter(([_, enabled]) => enabled)
        .map(([key]) => key);

      const serviceCreators = await Promise.all(
        enabledKeys.map(async (key) => {
          const definition = options.definitions[key];
          const { create } = await definition.load();

          return { key, create };
        }),
      );

      try {
        const loaded: Partial<TServices> = {};

        scope.run(() =>
          app.runWithContext(() => {
            for (const { key, create } of serviceCreators) {
              loaded[key] = create(
                toValue(options.deps),
              ) as TServices[typeof key];
            }
          }),
        );

        services.value = loaded ?? {};
        isReady.value = true;

        onCleanup(() => {
          console.log("[useLazyServices] Cleaning up services");
          scope.stop();
        });
      } catch (error) {
        console.error("[useLazyServices] Failed to load services:", error);
        services.value = {};
        isReady.value = true;
      }
    },
    { immediate: true },
  );

  return {
    services,
    isReady,
  };
}
